import { atlasAuthApi } from '../../services/api/atlasAuthApi';
import type {
  PresetId,
  RosteringAcceptResult,
  RosteringObjectiveId,
  RosteringProposal,
  RosteringRouteSnapshot,
  RulesCustomerSettings,
  RulesDriver,
  RulesDuty,
  RulesPreset,
  RuleSet,
  ValidationResultV1,
} from './types';

const BASE = '/rules-engine';

export async function fetchRulesPresets(): Promise<RulesPreset[]> {
  const { data } = await atlasAuthApi.get<RulesPreset[]>(`${BASE}/presets`);
  return data;
}

export async function fetchRulesSettings(): Promise<RulesCustomerSettings> {
  const { data } = await atlasAuthApi.get<RulesCustomerSettings>(`${BASE}/settings`);
  return data;
}

export async function patchRulesSettings(
  body: Partial<RulesCustomerSettings>
): Promise<RulesCustomerSettings> {
  const { data } = await atlasAuthApi.patch<RulesCustomerSettings>(`${BASE}/settings`, body);
  return data;
}

export async function fetchRulesDrivers(): Promise<RulesDriver[]> {
  const { data } = await atlasAuthApi.get<RulesDriver[]>(`${BASE}/drivers`);
  return data;
}

export async function createRulesDriver(body: {
  display_name: string;
  external_driver_id?: string;
  status?: 'active' | 'inactive';
}): Promise<RulesDriver> {
  const { data } = await atlasAuthApi.post<RulesDriver>(`${BASE}/drivers`, body);
  return data;
}

export async function updateRulesDriver(
  id: string,
  body: Partial<{ display_name: string; external_driver_id: string; status: 'active' | 'inactive' }>
): Promise<RulesDriver> {
  const { data } = await atlasAuthApi.patch<RulesDriver>(`${BASE}/drivers/${id}`, body);
  return data;
}

export async function fetchRulesDuties(): Promise<RulesDuty[]> {
  const { data } = await atlasAuthApi.get<RulesDuty[]>(`${BASE}/duties`);
  return data;
}

export async function cancelRulesDuty(id: string): Promise<RulesDuty> {
  const { data } = await atlasAuthApi.post<RulesDuty>(`${BASE}/duties/${id}/cancel`);
  return data;
}

export async function validateAndPersistDuty(body: {
  duty_id?: string;
  driver_id?: string | null;
  vehicle_id?: string | null;
  start_at: string;
  end_at: string;
  breaks?: Array<{ start_at: string; end_at: string }>;
  required_vehicle_tags?: string[];
  timezone?: string;
  overrides?: Array<{ ruleId: string; reason: string }>;
  persist?: boolean;
}): Promise<ValidationResultV1> {
  const { data } = await atlasAuthApi.post<ValidationResultV1>(`${BASE}/validate-duty`, body);
  return data;
}

export async function fetchRuleSets(): Promise<RuleSet[]> {
  const { data } = await atlasAuthApi.get<RuleSet[]>(`${BASE}/rule-sets`);
  return data;
}

export async function createRuleSetFromPreset(preset: PresetId, name?: string): Promise<RuleSet> {
  const { data } = await atlasAuthApi.post<RuleSet>(`${BASE}/rule-sets`, { preset, name });
  return data;
}

export async function updateRuleSet(
  id: string,
  body: Partial<Pick<RuleSet, 'name' | 'category' | 'settings'>>
): Promise<RuleSet> {
  const { data } = await atlasAuthApi.patch<RuleSet>(`${BASE}/rule-sets/${id}`, body);
  return data;
}

export async function fetchVehicleTags(): Promise<Array<{ vehicle_id: string; capability_tags: string[] }>> {
  const { data } = await atlasAuthApi.get<Array<{ vehicle_id: string; capability_tags: string[] }>>(`${BASE}/vehicle-tags`);
  return data;
}

export async function upsertVehicleTags(
  vehicle_id: string,
  capability_tags: string[]
): Promise<{ vehicle_id: string; capability_tags: string[] }> {
  const { data } = await atlasAuthApi.put(`${BASE}/vehicle-tags`, { vehicle_id, capability_tags });
  return data;
}

export async function deleteVehicleTags(vehicle_id: string): Promise<void> {
  await atlasAuthApi.delete(`${BASE}/vehicle-tags/${vehicle_id}`);
}

export async function proposeRoster(body: {
  service_date: string;
  timezone?: string;
  route_snapshots: RosteringRouteSnapshot[];
  objective?: RosteringObjectiveId;
}): Promise<RosteringProposal> {
  const { data } = await atlasAuthApi.post<RosteringProposal>(`${BASE}/rostering/propose`, body);
  return data;
}

export async function acceptRoster(body: {
  timezone?: string;
  assignments: Array<{
    demand_id: string;
    driver_id?: string | null;
    vehicle_id?: string | null;
    route_ref?: string | null;
    start_at: string;
    end_at: string;
    required_vehicle_tags?: string[];
    overrides?: Array<{ ruleId: string; reason: string }>;
  }>;
}): Promise<{ results: RosteringAcceptResult[] }> {
  const { data } = await atlasAuthApi.post<{ results: RosteringAcceptResult[] }>(
    `${BASE}/rostering/accept`,
    body
  );
  return data;
}
